import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MapLibreMap } from "@mapka/react-maplibre-map";
import { useAppSelector } from "../hooks/redux.js";
import { memo, useEffect, useRef } from "react";
import { Flex } from "@mantine/core";
import { isEqual } from "es-toolkit/compat";
import { useStyleEditor } from "./StyleEditorContext.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { getCurrentSpec, getPrimaryVersionSpec, getSecondaryVersionSpec } from "../utils/selectors.js";
import { getApiKey } from "../reducers/apiKeyReducer.js";
import { Map as MapkaMap } from "@mapka/maplibre-gl-sdk";
MapkaMap.env = import.meta.env.VITE_DEPLOY_ENV;
const mapStyle = {
    border: "2px solid light-dark(black, white)",
    borderRadius: "12px",
    overflow: "hidden",
};
export const StyleEditorDiffMap = memo(() => {
    const previous = useRef(null);
    const previousSecondary = useRef(null);
    const { map, setMap, secondaryMap, setSecondaryMap, } = useStyleEditor();
    const apiKey = useAppSelector(getApiKey);
    const primary = useAppSelector(getPrimaryVersionSpec);
    const current = useAppSelector(getCurrentSpec);
    const secondary = useAppSelector(getSecondaryVersionSpec);
    const currentOrPrimary = primary ?? current;
    useEffect(() => {
        return () => {
            previous.current = currentOrPrimary;
        };
    }, [currentOrPrimary]);
    useEffect(() => {
        return () => {
            previousSecondary.current = secondary;
        };
    }, [secondary]);
    useEffect(() => {
        if (!map)
            return;
        if (!previous.current)
            return;
        if (!isEqual(previous.current.bearing, currentOrPrimary.bearing)) {
            map.setBearing(currentOrPrimary.bearing ?? 0);
        }
        if (!isEqual(previous.current.pitch, currentOrPrimary.pitch)) {
            map.setPitch(currentOrPrimary.pitch ?? 0);
        }
        if (!isEqual(previous.current.zoom, currentOrPrimary.zoom)) {
            map.setZoom(currentOrPrimary.zoom ?? 0);
        }
        if (!isEqual(previous.current.center, currentOrPrimary.center)) {
            map.setCenter(currentOrPrimary.center ?? [0, 0]);
        }
        if (!isEqual(previous.current.metadata?.mapka?.markers, current.metadata?.mapka?.markers)) {
            map.removeMarkers();
            map.removePopups();
            map.addMarkers(current.metadata?.mapka?.markers ?? []);
        }
    }, [map, currentOrPrimary, previous]);
    useEffect(() => {
        if (!secondaryMap)
            return;
        if (!secondary)
            return;
        if (!previousSecondary.current)
            return;
        if (!isEqual(secondary.bearing, previousSecondary.current.bearing)) {
            secondaryMap.setBearing(secondary.bearing ?? 0);
        }
        if (!isEqual(secondary.pitch, previousSecondary.current.pitch)) {
            secondaryMap.setPitch(secondary.pitch ?? 0);
        }
        if (!isEqual(secondary.zoom, previousSecondary.current.zoom)) {
            secondaryMap.setZoom(secondary.zoom ?? 0);
        }
        if (!isEqual(secondary.center, previousSecondary.current.center)) {
            secondaryMap.setCenter(secondary.center ?? [0, 0]);
        }
    }, [map, previousSecondary, secondary]);
    return (_jsx(Flex, { h: "100%", flex: "1 1 50%", style: mapStyle, children: _jsxs(ErrorBoundary, { children: [_jsx(MapLibreMap, { BaseMap: MapkaMap, apiKey: apiKey, onMapLoaded: setMap, height: "100%", width: "100%", style: currentOrPrimary }, "current-map"), secondary && (_jsx(MapLibreMap, { BaseMap: MapkaMap, apiKey: apiKey, onMapLoaded: setSecondaryMap, height: "100%", width: "100%", style: secondary }, "secondary-map"))] }) }));
});
StyleEditorDiffMap.displayName = "StyleMap";
